// This transport-independent client also runs in the focused Node test harness.
// Keep modern shared protocol/rules imports in the isolated browser module.
export function compareDecimal (left, right) {
  left = left || '0'
  right = right || '0'
  if (!/^(0|[1-9][0-9]*)$/.test(left) || !/^(0|[1-9][0-9]*)$/.test(right)) throw new Error('Invalid decimal ID')
  return left.length === right.length ? (left === right ? 0 : left > right ? 1 : -1) : left.length > right.length ? 1 : -1
}

function nextDecimal (value) {
  const digits = value.split('')
  let carry = 1
  for (let index = digits.length - 1; index >= 0 && carry; index--) {
    const digit = Number(digits[index]) + 1
    digits[index] = String(digit % 10)
    carry = digit === 10 ? 1 : 0
  }
  if (carry) digits.unshift('1')
  return digits.join('')
}

export function isCausallyFresh (personal, table, actionId) {
  return !!(personal && table && compareDecimal(personal.lastSettledRollSequence, table.lastSettledRollSequence) >= 0 && compareDecimal(personal.lastActionId, actionId) >= 0)
}

export function randomHex (bytes) {
  const values = new Uint8Array(bytes)
  window.crypto.getRandomValues(values)
  return Array.prototype.map.call(values, value => ('0' + value.toString(16)).slice(-2)).join('')
}

export function requestUUID () {
  const value = randomHex(16).split('')
  value[12] = '4'
  value[16] = (8 + parseInt(value[16], 16) % 4).toString(16)
  const hex = value.join('')
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-')
}

export function initialCrapsState () {
  return { table: null, personal: null, owner: null, connection: 'connecting', privateStatus: 'signedOut', capabilities: null, economy: null, pending: null, receipt: null, requiredActionId: null, error: '', serverOffset: 0, fetchedAt: 0, snap: 0 }
}

function isPreExecutionRejection (error) {
  const codes = {
    400: ['INVALID_REQUEST', 'INVALID_REQUEST_KEY', 'INVALID_HAND_ID', 'INVALID_WINDOW_ID', 'INVALID_INTENT', 'INVALID_JSON'],
    401: ['WEBSITE_LOGIN_REQUIRED'],
    403: ['FORBIDDEN', 'CSRF_REQUIRED', 'UNTRUSTED_ORIGIN'],
    415: ['JSON_REQUIRED']
  }
  return error.retryable !== true && !!codes[error.status] && codes[error.status].indexOf(error.code) !== -1
}

export function createCrapsClient (options) {
  const state = initialCrapsState()
  let generation = 0
  let stopped = false
  let unsubscribe
  let publicFlight
  let privateFlight
  let capabilityGeneration = 0
  let refreshTimer
  let highest = '0'
  let connected = false
  const emit = patch => { Object.assign(state, patch); if (options.onChange) options.onChange(state) }
  const savePending = () => {
    if (!options.storage || !state.owner) return
    try {
      const key = 'craps:pending:' + state.owner
      if (state.pending) options.storage.setItem(key, JSON.stringify({ savedAt: Date.now(), pending: state.pending }))
      else { options.storage.removeItem(key) }
    } catch (_) {}
  }
  function setOwner (owner) {
    owner = owner || null
    if (owner === state.owner) return
    if (options.storage && state.owner) { try { options.storage.removeItem('craps:pending:' + state.owner) } catch (_) {} }
    generation++
    privateFlight = null
    emit({ owner, personal: null, pending: null, receipt: null, requiredActionId: null, capabilities: null, economy: null, privateStatus: owner ? 'loading' : 'signedOut', error: '' })
    if (owner && options.storage) {
      try {
        const saved = JSON.parse(options.storage.getItem('craps:pending:' + owner))
        // A reload can interrupt the first POST after it has committed.
        if (saved && Date.now() - saved.savedAt < 86400000 && saved.pending.request && /^[0-9a-f-]{36}$/.test(saved.pending.request.requestKey)) emit({ pending: Object.assign({}, saved.pending, { status: 'unknown', ambiguous: true }) })
      } catch (_) {}
    }
  }
  function schedulePublic () {
    if (refreshTimer || stopped) return
    refreshTimer = setTimeout(() => { refreshTimer = null; refreshPublic() }, options.coalesceMs === undefined ? 250 : options.coalesceMs)
  }
  function invalidate (event) {
    if (stopped || !event || typeof event.revision !== 'string') return
    try {
      if (compareDecimal(event.revision, highest) <= 0) return
      if (highest !== '0' && event.revision !== nextDecimal(highest)) emit({ connection: 'reconnecting', snap: state.snap + 1 })
      highest = event.revision
      schedulePublic()
    } catch (_) { emit({ connection: 'stale' }); schedulePublic() }
  }
  function refreshPublic () {
    if (stopped) return Promise.resolve()
    if (publicFlight) return publicFlight
    publicFlight = (async () => {
      let received = false
      try {
        const snapshot = await options.request('/table')
        if (stopped) return
        if (state.table && compareDecimal(snapshot.revision, state.table.revision) < 0) return
        const previous = state.table
        received = true
        const caughtUp = compareDecimal(snapshot.revision, highest) >= 0
        // Contiguous aggregate traffic is coalesced without invalidating a
        // causally fresh account. A gap or reconnect has already left "live"
        // and must catch up completely before controls become live again.
        emit({ table: snapshot, serverOffset: Date.parse(snapshot.serverTime) - Date.now(), fetchedAt: Date.now(), connection: connected ? (caughtUp || state.connection === 'live' ? 'live' : 'reconnecting') : 'stale' })
        if (compareDecimal(snapshot.revision, highest) > 0) highest = snapshot.revision
        if (state.owner && previous && compareDecimal(snapshot.lastSettledRollSequence, previous.lastSettledRollSequence) > 0) refreshPrivate()
      } catch (_) { if (!stopped) emit({ connection: state.table ? 'stale' : 'connecting' }) } finally {
        publicFlight = null
        if (received && state.table && compareDecimal(highest, state.table.revision) > 0) schedulePublic()
      }
    })()
    return publicFlight
  }
  function refreshPrivate () {
    if (stopped || !state.owner) return Promise.resolve()
    if (privateFlight) return privateFlight
    const ownGeneration = generation
    const owner = state.owner
    const flight = (async () => {
      try {
        const personal = await options.request('/me', { private: true })
        if (stopped || generation !== ownGeneration || state.owner !== owner) return
        if (personal.owner !== owner) {
          emit({ personal: null, capabilities: null, economy: null, pending: null, receipt: null, privateStatus: 'reauth' })
          return
        }
        emit({ personal, privateStatus: 'ready' })
        if (!state.capabilities) {
          const ownCapabilityGeneration = capabilityGeneration
          try {
            const capabilities = await options.request('/capabilities', { private: true })
            if (!stopped && generation === ownGeneration && capabilityGeneration === ownCapabilityGeneration) emit({ capabilities })
          } catch (_) {}
        }
        if (stopped || generation !== ownGeneration) return
        if (state.capabilities && ['moderator', 'operator'].indexOf(state.capabilities.role) !== -1) {
          const ownCapabilityGeneration = capabilityGeneration
          try {
            const economy = await options.request('/economy', { private: true })
            if (!stopped && generation === ownGeneration && capabilityGeneration === ownCapabilityGeneration) emit({ economy })
          } catch (error) {
            if (!stopped && generation === ownGeneration) {
              emit({ economy: null })
              if (error.status === 401 || error.status === 403) { capabilityGeneration++; emit({ capabilities: null }) }
            }
          }
        } else emit({ economy: null })
      } catch (error) {
        if (!stopped && generation === ownGeneration) emit({ personal: error.status === 401 ? null : state.personal, capabilities: error.status === 401 ? null : state.capabilities, economy: null, privateStatus: error.status === 401 ? 'reauth' : 'stale' })
      } finally { if (privateFlight === flight) privateFlight = null }
    })()
    privateFlight = flight
    return flight
  }
  function currentPending (pending, ownGeneration) {
    return !stopped && generation === ownGeneration && state.pending && state.pending.request === pending.request
  }
  async function deliver () {
    const pending = state.pending
    if (!pending || pending.status === 'sending' || pending.status === 'checking') return
    const ownGeneration = generation
    emit({ pending: Object.assign({}, pending, { status: 'sending' }), error: '' }); savePending()
    try {
      const response = await options.request(pending.endpoint, { method: 'POST', private: true, csrfToken: state.personal && state.personal.csrfToken, body: pending.request })
      if (!currentPending(pending, ownGeneration)) return
      acceptReceipt(response.receipt)
    } catch (error) {
      if (!currentPending(pending, ownGeneration)) return
      // A later rejection cannot prove an earlier lost response did not commit.
      if (!pending.ambiguous && isPreExecutionRejection(error)) {
        emit({ pending: null, error: error.code === 'FORBIDDEN' ? 'You no longer have permission for this action.' : 'Request rejected: ' + error.code + '. Refresh your account before trying again.' })
      } else {
        emit({ pending: Object.assign({}, pending, { status: 'unknown', ambiguous: true }), error: 'Receipt not confirmed. Check receipt or retry this same request.' })
      }
      savePending()
      if (error.status === 401) emit({ privateStatus: 'reauth', personal: null, capabilities: null, economy: null })
      if (error.code === 'FORBIDDEN' || error.code === 'CSRF_REQUIRED') {
        if (error.code === 'FORBIDDEN') { capabilityGeneration++; emit({ capabilities: null, economy: null }) }
        // An earlier capabilities read must finish before this fresh read.
        await Promise.resolve(privateFlight)
        if (!stopped && generation === ownGeneration) await refreshPrivate()
      }
    }
  }
  function acceptReceipt (receipt) {
    if (!receipt || !receipt.actionId) throw new Error('Missing durable receipt')
    emit({ receipt, requiredActionId: compareDecimal(receipt.actionId, state.requiredActionId) > 0 ? receipt.actionId : state.requiredActionId, pending: null, error: '' })
    savePending()
    // A read already in flight may predate this action. Follow it with a fresh read.
    Promise.resolve(privateFlight).then(() => refreshPrivate())
    refreshPublic()
  }
  async function submit (intent, endpoint = '/actions') {
    if (state.pending) throw new Error('Resolve the unresolved request before another action')
    if (!state.personal || state.privateStatus !== 'ready' || !state.table) throw new Error('Sign in and refresh your account first')
    const request = { requestKey: (options.uuid || requestUUID)(), handId: state.table.databaseHandId || null, windowId: state.table.windowId || null, intent: JSON.parse(JSON.stringify(intent)) }
    emit({ pending: { request, endpoint, status: 'ready', ambiguous: false } }); savePending()
    return deliver()
  }
  async function checkReceipt () {
    const pending = state.pending
    if (!pending || pending.status === 'sending' || pending.status === 'checking') return
    const ownGeneration = generation
    emit({ pending: Object.assign({}, pending, { status: 'checking' }), error: '' }); savePending()
    try {
      const response = await options.request('/receipts?requestKey=' + encodeURIComponent(pending.request.requestKey), { private: true })
      if (currentPending(pending, ownGeneration)) acceptReceipt(response.receipt || response)
    } catch (_) {
      if (!currentPending(pending, ownGeneration)) return
      emit({ pending: Object.assign({}, pending, { status: 'unknown' }), error: 'No receipt confirmed yet. Retry keeps the original request and window.' })
      savePending()
    }
  }
  async function start () {
    try {
      if (options.subscribe) {
        unsubscribe = await options.subscribe(invalidate, status => {
          connected = status === 'live'
          emit({ connection: connected ? 'reconnecting' : 'stale', snap: state.snap + 1 })
          if (connected) { refreshPublic(); refreshPrivate() }
        })
      }
      connected = !!options.subscribe
    } catch (_) { connected = false }
    if (stopped) { if (unsubscribe) unsubscribe(); return }
    await refreshPublic()
    if (state.table && compareDecimal(highest, state.table.revision) > 0) await refreshPublic()
  }
  function dispose () {
    stopped = true
    generation++
    clearTimeout(refreshTimer)
    if (unsubscribe) unsubscribe()
  }
  return { state, start, setOwner, refreshPublic, refreshPrivate, submit, retry: deliver, checkReceipt, dispose }
}

let modulePromise
export function loadSceneModule () {
  if (modulePromise) return modulePromise
  modulePromise = new Promise((resolve, reject) => {
    const controller = new window.AbortController()
    let script
    let settled = false
    // One deadline covers manifest headers, its JSON body and the module script.
    const timeout = setTimeout(() => finish(new Error('The 3D module timed out')), 20000)
    function finish (error, api) {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      if (script) {
        script.onload = null
        script.onerror = null
        if (script.parentNode) script.parentNode.removeChild(script)
      }
      if (error) { controller.abort(); reject(error) } else resolve(api)
    }
    Promise.resolve().then(() => window.fetch('/static/dice3d/manifest.json', { cache: 'no-store', credentials: 'omit', signal: controller.signal })).then(response => {
      if (settled) return
      if (!response.ok) throw new Error('The 3D module manifest could not be loaded')
      return response.json()
    }).then(manifest => {
      if (settled) return
      if (!manifest || manifest.protocolVersion !== 1 || !/^scene-[a-zA-Z0-9_-]+\.js$/.test(manifest.module)) throw new Error('Incompatible scene manifest')
      script = document.createElement('script')
      script.type = 'module'
      script.src = '/static/dice3d/' + manifest.module
      script.onload = () => {
        const api = window.SwiftorlandCrapsScene
        if (!api || api.protocolVersion !== manifest.protocolVersion || api.version !== manifest.version || !api.previewWager) finish(new Error('Scene version mismatch; reload the page'))
        else finish(null, api)
      }
      script.onerror = () => finish(new Error('The 3D module could not be loaded'))
      document.head.appendChild(script)
    }).catch(error => finish(error))
  }).catch(error => { modulePromise = null; throw error })
  return modulePromise
}



// WEBPACK FOOTER //
// ./src/lib/craps-client.js